import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { rupee, TIME_WINDOWS, nextDays, SOURCES } from "@/lib/constants";
import { toast } from "sonner";
import { Loader2, Plus, X } from "lucide-react";
import { FilterBar, inRange, uniq } from "@/components/admin/FilterBar";

export default function AdminBookings() {
    const [rows, setRows] = useState(null);
    const [services, setServices] = useState([]);
    const [areas, setAreas] = useState([]);
    const [q, setQ] = useState("");
    const [statusF, setStatusF] = useState("");
    const [showNew, setShowNew] = useState(false);
    const nav = useNavigate();

    const load = () => api.get("/bookings").then((r) => setRows(r.data)).catch(() => setRows([]));
    useEffect(() => {
        load();
        api.get("/services").then((r) => setServices(r.data)).catch(() => {});
        api.get("/service-areas").then((r) => setAreas(r.data)).catch(() => {});
    }, []);

    const [svcF, setSvcF] = useState("");
    const [areaF, setAreaF] = useState("");
    const [payF, setPayF] = useState("");
    const [dates, setDates] = useState({ from: "", to: "" });
    const filtered = (rows || []).filter((b) => {
        if (statusF && b.status !== statusF) return false;
        if (svcF && b.service_name !== svcF) return false;
        if (areaF && b.area_name !== areaF) return false;
        if (payF === "due" && !(b.estimate?.amount_due > 0)) return false;
        if (payF === "paid" && !b.invoice) return false;
        if (!inRange(b.preferred_date || b.created_at, dates)) return false;
        if (q) {
            const s = `${b.booking_id} ${b.name} ${b.phone} ${b.service_name} ${b.area_name} ${b.professional?.name || ""}`.toLowerCase();
            if (!s.includes(q.toLowerCase())) return false;
        }
        return true;
    });

    const statuses = uniq((rows || []).map((b) => b.status));

    return (
        <div>
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-2xl font-extrabold text-navy">Bookings</h1>
                    <p className="text-sm text-navy/55">{filtered.length} bookings</p>
                </div>
                <button data-testid="admin-new-booking-btn" onClick={() => setShowNew(true)} className="flex items-center gap-2 rounded-full bg-orange px-4 py-2.5 text-sm font-bold text-white transition-transform active:scale-95"><Plus size={17} /> New booking</button>
            </div>

            <FilterBar testid="booking-filter" q={q} setQ={setQ} placeholder="Search by ID, name, phone, pro…" dates={dates} setDates={setDates}
                selects={[
                    { key: "status", label: "All statuses", value: statusF, set: setStatusF, options: statuses },
                    { key: "service", label: "All services", value: svcF, set: setSvcF, options: uniq((rows || []).map((b) => b.service_name)) },
                    { key: "area", label: "All areas", value: areaF, set: setAreaF, options: uniq((rows || []).map((b) => b.area_name)) },
                    { key: "payment", label: "Any payment", value: payF, set: setPayF, options: [{ value: "due", label: "Amount due" }, { value: "paid", label: "Invoiced / paid" }] },
                ]}
                onClear={() => { setQ(""); setStatusF(""); setSvcF(""); setAreaF(""); setPayF(""); setDates({ from: "", to: "" }); }} />

            <div className="mt-4 overflow-hidden rounded-2xl border border-navy/10 bg-white">
                {rows === null && <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div>}
                <div className="overflow-x-auto homiie-scroll">
                    <table className="w-full text-sm">
                        <thead className="bg-navy/[0.03] text-left text-xs uppercase tracking-wide text-navy/50">
                            <tr>
                                <th className="px-4 py-3">Booking</th>
                                <th className="px-4 py-3">Customer</th>
                                <th className="px-4 py-3">Service</th>
                                <th className="px-4 py-3">Area</th>
                                <th className="px-4 py-3">When</th>
                                <th className="px-4 py-3">Pro</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((b) => (
                                <tr key={b.id} onClick={() => nav(`/admin/bookings/${b.id}`)} data-testid={`admin-booking-${b.booking_id}`} className="cursor-pointer border-t border-navy/5 transition-colors hover:bg-orange/5">
                                    <td className="px-4 py-3 font-heading font-bold text-navy">{b.booking_id}</td>
                                    <td className="px-4 py-3"><div className="font-medium text-navy">{b.name}</div><div className="text-xs text-navy/45">{b.phone}</div></td>
                                    <td className="px-4 py-3 text-navy/70">{b.service_name}<div className="text-xs text-navy/45">{b.problem}</div></td>
                                    <td className="px-4 py-3 text-navy/70">{b.area_name}</td>
                                    <td className="px-4 py-3 text-navy/60">{b.preferred_date}<div className="text-xs text-navy/45">{b.preferred_window}</div></td>
                                    <td className="px-4 py-3 text-navy/70">{b.professional?.name || <span className="text-orange-dark">Unassigned</span>}</td>
                                    <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
                                    <td className="px-4 py-3 font-semibold text-navy">{b.payment ? rupee(b.payment.amount) : b.estimate ? rupee(b.estimate.total) : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {rows && filtered.length === 0 && <div className="py-12 text-center text-sm text-navy/40">No bookings match your filters.</div>}
                </div>
            </div>

            {showNew && <NewBooking services={services.filter((s) => s.enabled)} areas={areas.filter((a) => a.enabled)} onClose={() => setShowNew(false)} onDone={() => { setShowNew(false); load(); }} />}
        </div>
    );
}

function NewBooking({ services, areas, onClose, onDone }) {
    const days = nextDays(7);
    const [f, setF] = useState({ service_id: services[0]?.id || "", problem: "", name: "", phone: "", address_line: "", area_id: areas[0]?.id || "", society: "", preferred_date: days[0].value, preferred_window: TIME_WINDOWS[1], source: "WhatsApp" });
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
    const svc = services.find((s) => s.id === f.service_id);

    const submit = async () => {
        if (!f.name || !f.phone || !f.address_line || !f.problem) return toast.error("Fill all required fields");
        setBusy(true);
        try { await api.post("/admin/bookings", f); toast.success("Booking created"); onDone(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4" onClick={onClose}>
            <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 homiie-scroll" onClick={(e) => e.stopPropagation()}>
                <div className="mb-4 flex items-center justify-between"><h2 className="font-heading text-xl font-extrabold text-navy">New booking</h2><button onClick={onClose}><X size={20} className="text-navy/50" /></button></div>
                <div className="grid grid-cols-2 gap-3">
                    <Sel label="Service" value={f.service_id} onChange={set("service_id")} options={services.map((s) => [s.id, s.name])} />
                    <Sel label="Problem" value={f.problem} onChange={set("problem")} options={(svc?.problems || []).map((p) => [p, p])} placeholder="Select problem" />
                    <Inp label="Name *" value={f.name} onChange={set("name")} />
                    <Inp label="Phone *" value={f.phone} onChange={set("phone")} />
                    <div className="col-span-2"><Inp label="Address *" value={f.address_line} onChange={set("address_line")} /></div>
                    <Sel label="Area" value={f.area_id} onChange={set("area_id")} options={areas.map((a) => [a.id, a.name])} />
                    <Inp label="Society" value={f.society} onChange={set("society")} />
                    <Sel label="Date" value={f.preferred_date} onChange={set("preferred_date")} options={days.map((d) => [d.value, d.label])} />
                    <Sel label="Time" value={f.preferred_window} onChange={set("preferred_window")} options={TIME_WINDOWS.map((w) => [w, w])} />
                    <Sel label="Source" value={f.source} onChange={set("source")} options={SOURCES.map((s) => [s, s])} />
                </div>
                <button data-testid="submit-new-booking" disabled={busy} onClick={submit} className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-orange py-3 text-sm font-bold text-white disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />} CREATE BOOKING</button>
            </div>
        </div>
    );
}

function Inp({ label, value, onChange }) {
    return <div><label className="mb-1 block text-xs font-semibold text-navy/60">{label}</label><input value={value} onChange={onChange} className="w-full rounded-xl border border-navy/15 px-3 py-2.5 text-sm outline-none focus:border-orange" /></div>;
}
function Sel({ label, value, onChange, options, placeholder }) {
    return <div><label className="mb-1 block text-xs font-semibold text-navy/60">{label}</label><select value={value} onChange={onChange} className="w-full rounded-xl border border-navy/15 bg-white px-3 py-2.5 text-sm outline-none focus:border-orange">{placeholder && <option value="">{placeholder}</option>}{options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>;
}
